import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useStore } from '../../store';

interface NodeContentProps {
  id: string;
  text: string;
  timeEstimate: string;
  children: string[];
}

// Validate time format: 1h, 1d, 1w, 1m
const isValidTimeFormat = (value: string): boolean => {
  if (!value) return true; // Empty is valid
  return /^\d+[hdwm]$/.test(value);
};

export function NodeContent({ id, text, timeEstimate }: NodeContentProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const [isEditingText, setIsEditingText] = useState(false);
  const [timeValue, setTimeValue] = useState(timeEstimate || '');
  const [timeError, setTimeError] = useState(false);
  const updateNode = useStore((state) => state.updateNode);

  // Handle text blur (save)
  const handleTextBlur = useCallback(() => {
    setIsEditingText(false);
    if (textRef.current) {
      // Use innerText to preserve line breaks
      const newText = textRef.current.innerText || '';
      if (newText !== text) {
        updateNode(id, { text: newText });
      }
    }
  }, [id, text, updateNode]);

  // Handle text keydown (Escape to save, Enter for new line)
  const handleTextKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Escape to save and exit
      if (e.key === 'Escape') {
        e.preventDefault();
        textRef.current?.blur();
      }
      // Stop propagation to prevent node deletion while editing
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.stopPropagation();
      }
      // Allow Enter for new lines (don't prevent default)
    },
    []
  );

  // Handle time input change
  const handleTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTimeValue(value);

    if (isValidTimeFormat(value)) {
      setTimeError(false);
    } else {
      setTimeError(true);
    }
  }, []);

  // Handle time input blur (save if valid)
  const handleTimeBlur = useCallback(() => {
    if (isValidTimeFormat(timeValue)) {
      updateNode(id, { timeEstimate: timeValue });
      setTimeError(false);
    } else if (timeValue === '') {
      updateNode(id, { timeEstimate: '' });
      setTimeError(false);
    } else {
      // Invalid format - revert to previous value
      setTimeValue(timeEstimate || '');
      setTimeError(false);
    }
  }, [id, timeValue, timeEstimate, updateNode]);

  // Handle time input keydown
  const handleTimeKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        timeInputRef.current?.blur();
      }
      // Stop propagation to prevent node deletion while editing
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.stopPropagation();
      }
    },
    []
  );

  // Update text content when prop changes (but not while editing)
  useEffect(() => {
    if (textRef.current && !isEditingText) {
      // Use innerText to preserve line breaks
      textRef.current.innerText = text || '';
    }
  }, [text, isEditingText]);

  // Update time value when prop changes
  useEffect(() => {
    setTimeValue(timeEstimate || '');
  }, [timeEstimate]);

  return (
    <>
      <div
        ref={textRef}
        className="node-header nodrag"
        contentEditable
        suppressContentEditableWarning
        onFocus={() => setIsEditingText(true)}
        onBlur={handleTextBlur}
        onKeyDown={handleTextKeyDown}
        data-placeholder="Double-click to edit"
      >
        {text}
      </div>
      <div className="node-footer">
        <input
          ref={timeInputRef}
          type="text"
          className={`time-input nodrag ${timeError ? 'error' : ''}`}
          value={timeValue}
          onChange={handleTimeChange}
          onBlur={handleTimeBlur}
          onKeyDown={handleTimeKeyDown}
          placeholder="1h"
          title="Format: 1h, 1d, 1w, 1m"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </>
  );
}
